import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Leaf, FolderOpen, Clock, User, LogOut, 
  Database, Settings, ChevronRight, Target, Package,
  BarChart3, Lightbulb, Lock, Unlock, Trash2, AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, project: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await api.post('/projects', {
        name: newProjectName,
        description: newProjectDesc
      });
      toast.success('Project created!');
      setProjects([...projects, response.data]);
      setShowNewProject(false);
      setNewProjectName('');
      setNewProjectDesc('');
      navigate(`/project/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async () => {
    if (!deleteDialog.project) return;
    
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteDialog.project.id}`);
      setProjects(projects.filter(p => p.id !== deleteDialog.project.id));
      toast.success('Project deleted successfully');
      setDeleteDialog({ open: false, project: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const phaseLabels = {
    1: { name: 'Goal & Scope', icon: Target },
    2: { name: 'Inventory (LCI)', icon: Package },
    3: { name: 'Impact (LCIA)', icon: BarChart3 },
    4: { name: 'Interpretation', icon: Lightbulb }
  };

  const getPhaseStatus = (project) => {
    const statuses = project.phase_statuses;
    for (let i = 4; i >= 1; i--) {
      if (statuses[String(i)] === 'locked') {
        return { phase: i, label: `Phase ${i} Complete` };
      }
    }
    return { phase: project.current_phase, label: `Phase ${project.current_phase} In Progress` };
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/saarv-logo.svg" alt="Saarv" className="h-14 w-auto" />
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/datasets')}
              data-testid="datasets-nav-btn"
              className="text-muted-foreground hover:text-primary hover:bg-secondary"
            >
              <Database className="h-4 w-4 mr-2" />
              Datasets
            </Button>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{user?.name}</span>
              <Badge variant="outline" className="text-xs capitalize border-primary/50 text-primary bg-secondary">{user?.role}</Badge>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={logout}
              data-testid="logout-btn"
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Projects</h2>
            <p className="text-muted-foreground mt-1">Manage your LCA studies</p>
          </div>
          
          <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
            <DialogTrigger asChild>
              <Button data-testid="create-project-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border border-border">
              <DialogHeader>
                <DialogTitle className="text-foreground">Create New LCA Project</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Start a new ISO 14040/14044 compliant life cycle assessment
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createProject} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name" className="text-foreground">Project Name</Label>
                  <Input
                    id="project-name"
                    placeholder="e.g., Solar Panel LCA 2024"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    required
                    data-testid="new-project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project-desc">Description (Optional)</Label>
                  <Textarea
                    id="project-desc"
                    placeholder="Brief description of the LCA study..."
                    value={newProjectDesc}
                    onChange={(e) => setNewProjectDesc(e.target.value)}
                    rows={3}
                    data-testid="new-project-desc-input"
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowNewProject(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="create-project-submit-btn">
                    {creating ? 'Creating...' : 'Create Project'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, project: open ? deleteDialog.project : null })}>
          <DialogContent className="bg-card border border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Project
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Are you sure you want to delete <span className="font-semibold text-foreground">"{deleteDialog.project?.name}"</span>? 
                This action cannot be undone and all associated data will be permanently removed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setDeleteDialog({ open: false, project: null })}
                className="border-border"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={deleteProject}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border border-border animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-20 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <FolderOpen className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Projects Yet</h3>
            <p className="text-muted-foreground mb-6">
              Create your first ISO-compliant LCA project to get started
            </p>
            <Button onClick={() => setShowNewProject(true)} data-testid="empty-create-project-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4 mr-2" />
              Create First Project
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, idx) => {
              const status = getPhaseStatus(project);
              return (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card 
                    className="bg-card border border-border cursor-pointer group hover:border-primary/50 hover:shadow-md transition-all relative"
                    onClick={() => navigate(`/project/${project.id}`)}
                    data-testid={`project-card-${project.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg text-foreground group-hover:text-primary transition-colors">
                            {project.name}
                          </CardTitle>
                          <CardDescription className="line-clamp-2 mt-1 text-muted-foreground">
                            {project.description || 'No description'}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Badge variant="outline" className="text-xs border-primary/50 text-primary bg-secondary">
                            v{project.version}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteDialog({ open: true, project });
                            }}
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Phase Progress */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                          <span>{status.label}</span>
                        </div>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4].map((phase) => {
                            const PhaseIcon = phaseLabels[phase].icon;
                            const isLocked = project.phase_statuses[String(phase)] === 'locked';
                            const isCurrent = project.current_phase === phase;
                            return (
                              <div
                                key={phase}
                                className={`flex-1 h-2 rounded-full transition-colors ${
                                  isLocked ? 'bg-primary' : isCurrent ? 'bg-accent' : 'bg-muted'
                                }`}
                                title={phaseLabels[phase].name}
                              />
                            );
                          })}
                        </div>
                      </div>

                      {/* Phase Icons */}
                      <div className="flex items-center justify-between pt-3 border-t border-border">
                        {[1, 2, 3, 4].map((phase) => {
                          const PhaseIcon = phaseLabels[phase].icon;
                          const isLocked = project.phase_statuses[String(phase)] === 'locked';
                          const isCurrent = project.current_phase === phase;
                          return (
                            <div
                              key={phase}
                              className={`p-2 rounded-lg ${
                                isLocked ? 'bg-secondary text-primary' : 
                                isCurrent ? 'bg-accent/20 text-accent' : 
                                'bg-muted text-muted-foreground'
                              }`}
                              title={phaseLabels[phase].name}
                            >
                              <PhaseIcon className="h-4 w-4" />
                            </div>
                          );
                        })}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(project.updated_at).toLocaleDateString()}
                        </div>
                        <ChevronRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
